'use client';

import { useEffect, useState } from 'react';

interface StrapiErrorPageProps {
  error?: Error & { digest?: string };
  reset?: () => void;
  message?: string;
  countdown?: number;
  maxRetries?: number;
}

// Persist retry count across component remounts using sessionStorage
const RETRY_COUNT_KEY = 'strapi-error-retry-count';

const setRetryCountStorage = (count: number) => {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(RETRY_COUNT_KEY, count.toString());
  } catch {
    // Ignore storage errors
  }
};

const clearRetryCount = () => {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(RETRY_COUNT_KEY);
  } catch {
    // Ignore storage errors
  }
};

export default function StrapiErrorPage({
  error,
  reset,
  message,
  countdown: initialCountdown = 30,
  maxRetries = 3
}: StrapiErrorPageProps) {
  const [retryCount, setRetryCount] = useState(0); // Always start fresh at 0
  const [countdown, setCountdown] = useState(initialCountdown);

  // Cleanup error state on unmount
  useEffect(() => {
    return () => {
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem(RETRY_COUNT_KEY);
        sessionStorage.removeItem('strapi-error-active');
      }
    };
  }, []);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const errorActive = sessionStorage.getItem('strapi-error-active');
      if (!errorActive) {
        // New error after app was working
        setRetryCount(0);
        sessionStorage.setItem(RETRY_COUNT_KEY, '0');
        sessionStorage.setItem('strapi-error-active', 'true');
      } else {
        // Error page is still active, restore retry count
        const stored = sessionStorage.getItem(RETRY_COUNT_KEY);
        if (stored) setRetryCount(Number(stored));
      }
    }
    // Close Next.js dev overlay if present
    const closeButton = document.querySelector('[data-nextjs-errors-dialog-close]');
    if (closeButton instanceof HTMLElement) {
      closeButton.click();
    }
    setCountdown(initialCountdown);
  }, [initialCountdown]);

  useEffect(() => {
    // Check if max retries reached
    if (retryCount >= maxRetries) {
      return;
    }

    // Reset countdown when component mounts or retryCount changes
    setCountdown(initialCountdown);

    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          const newCount = retryCount + 1;
          setRetryCount(newCount);
          setRetryCountStorage(newCount);
          // Force hard reload to bypass all caches
          window.location.reload();
          return initialCountdown;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [reset, initialCountdown, retryCount, maxRetries]);

  const handleRetry = () => {
    const newCount = retryCount + 1;
    setRetryCount(newCount);
    setRetryCountStorage(newCount);
    // Force hard reload to bypass all caches
    window.location.reload();
  };

  const handleClearRetries = () => {
    clearRetryCount();
    setRetryCount(0);
    // Force hard reload to bypass all caches
    window.location.reload();
  };

  const errorMessage = message || error?.message || 'Unable to retrieve data from CMS';
  const retriesExhausted = retryCount >= maxRetries;

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-900 via-red-800 to-red-900 flex items-center justify-center p-4">
      <div className="max-w-2xl mx-auto text-center">
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 shadow-2xl border border-white/20">
          <div className="text-8xl mb-6">
            <span>⚠️</span>
          </div>
          
          <h1 className="text-6xl font-bold mb-4 tracking-wider">
            500
          </h1>
          
          <h2 className="text-2xl font-semibold mb-6">
            CMS Data Service Unavailable
          </h2>
          
          <div className="mb-8 space-y-2">
            <div className="text-lg">
              <p className="break-words whitespace-pre-wrap">{errorMessage}</p>
            </div>
          </div>

          {!retriesExhausted ? (
            <div className="mb-6 p-4 bg-yellow-500/20 rounded-lg border border-yellow-500/30">
              <p className="text-lg font-semibold">
                ⏰ Auto-retrying in <span className="text-2xl font-bold">{countdown}</span> seconds...
              </p>
              <p className="text-sm opacity-80 mt-2">
                Attempt {retryCount + 1} of {maxRetries}
              </p>
            </div>
          ) : (
            <div className="mb-6 p-4 bg-red-500/20 rounded-lg border border-red-500/30">
              <p className="text-lg font-semibold">
                ⚠️ Auto-retry limit reached
              </p>
              <p className="text-sm opacity-80 mt-2">
                Please retry manually or contact support
              </p>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleRetry}
              className="px-6 py-3 bg-red-600 hover:bg-red-700 font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
            >
              🔄 Retry Now
            </button>
            {retriesExhausted && (
              <button
                onClick={handleClearRetries}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
              >
                🔄 Reset Counter
              </button>
            )}
            <a
              href="mailto:mfj@earthling.net"
              className="px-6 py-3 bg-white/20 hover:bg-white/30 font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg no-underline text-center"
            >
              📧 Contact Support
            </a>
          </div>
          
          <div className="mt-8 pt-6 border-t border-white/20">
            <div className="text-left">
              <div className="mb-3 font-semibold opacity-90">Technical Details</div>
              <div className="text-xs font-mono bg-black/30 rounded p-3 space-y-1 break-words opacity-80">
                <p className="whitespace-pre-wrap"><strong>Error:</strong> {errorMessage}</p>
                {error?.digest && <p><strong>Error ID:</strong> {error.digest}</p>}
                <p><strong>Component:</strong> Server-side data fetching</p>
                <p><strong>Timestamp:</strong> {new Date().toISOString()}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
